import isEqual from "lodash/isEqual";
import * as z from "zod";

import { HOLD_MESSAGE_MAX_LENGTH } from "components/HoldMessageVoiceFields";

// Hours without a stored value fall back to the same defaults the backend
// applies when it builds a dial session's calling window.
const DEFAULT_CALL_WINDOW_START = "9";
const DEFAULT_CALL_WINDOW_END = "17";

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  text: `${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`,
}));

// The backend casts these settings with int(), so a decimal or negative value
// would be rejected there rather than saved.
const wholeNumber = z
  .string()
  .trim()
  .min(1, "Required")
  .regex(/^\d+$/, "Enter a whole number");

const savedAttachmentSchema = z
  .object({ s3_key: z.string(), file_name: z.string() })
  .passthrough();

const voicemailSchema = z
  .union([
    savedAttachmentSchema,
    z
      .instanceof(File)
      .refine(
        file => file.name.toLowerCase().endsWith(".mp3"),
        "The voicemail must be an MP3 file"
      ),
  ])
  .nullable();

export const globalSettingsSchema = z.object({
  call_center: z
    .object({
      call_window_start: z.string(),
      call_window_end: z.string(),
      zendesk_inbound_number: z.string().nullable(),
      system_voicemail: voicemailSchema,
      hold_message: z
        .string()
        .trim()
        .max(
          HOLD_MESSAGE_MAX_LENGTH,
          `Hold message must be at most ${HOLD_MESSAGE_MAX_LENGTH} characters`
        ),
      hold_message_language: z.string().nullable(),
      hold_message_voice: z.string().nullable(),
    })
    .refine(
      ({ call_window_start, call_window_end }) =>
        Number(call_window_start) < Number(call_window_end),
      {
        path: ["call_window_end"],
        message: "The call window must end after it starts",
      }
    )
    // Mirrors the backend: language and voice are set together and need a
    // text, while a text alone predates them and keeps the default voice.
    .superRefine((values, ctx) => {
      const require = (name, label) =>
        ctx.addIssue({
          code: "custom",
          path: [name],
          message: `${label} is required`,
        });
      if (values.hold_message_language && !values.hold_message_voice) {
        require("hold_message_voice", "Voice");
      }
      if (values.hold_message_voice && !values.hold_message_language) {
        require("hold_message_language", "Language");
      }
      if (values.hold_message_language && !values.hold_message) {
        require("hold_message", "Hold message");
      }
    }),
  piracy: z
    .object({
      time_limit: wholeNumber,
      distance_limit_min: wholeNumber,
      distance_limit_mid: wholeNumber,
      distance_limit_max: wholeNumber,
    })
    // Mirrors the ordering check the backend enforces on save, so the admin
    // sees which band is wrong instead of a generic error.
    .refine(
      ({ distance_limit_min, distance_limit_mid }) =>
        Number(distance_limit_min) < Number(distance_limit_mid),
      {
        path: ["distance_limit_mid"],
        message: "Must be greater than the Green limit",
      }
    )
    .refine(
      ({ distance_limit_mid, distance_limit_max }) =>
        Number(distance_limit_mid) < Number(distance_limit_max),
      {
        path: ["distance_limit_max"],
        message: "Must be greater than the Yellow limit",
      }
    ),
});

/**
 * Maps the `SettingsService.getSettings(true)` object onto the form's values.
 *
 * Args:
 *   settings: `{ [category]: { [name]: { id, value } } }`, or undefined while
 *     the request is in flight.
 *
 * Returns:
 *   The form values. Every setting is a string except the voicemail, which
 *   is the parsed attachment record, and the Zendesk number, which is null
 *   when unset.
 */
export function toFormValues(settings) {
  const value = (category, name) => settings?.[category]?.[name]?.value || "";
  const voicemail = value("call_center", "system_voicemail");
  return {
    call_center: {
      call_window_start:
        value("call_center", "call_window_start") || DEFAULT_CALL_WINDOW_START,
      call_window_end:
        value("call_center", "call_window_end") || DEFAULT_CALL_WINDOW_END,
      zendesk_inbound_number:
        value("call_center", "zendesk_inbound_number") || null,
      system_voicemail: voicemail ? JSON.parse(voicemail) : null,
      hold_message: value("call_center", "hold_message"),
      hold_message_language:
        value("call_center", "hold_message_language") || null,
      hold_message_voice: value("call_center", "hold_message_voice") || null,
    },
    piracy: {
      time_limit: value("piracy", "time_limit"),
      distance_limit_min: value("piracy", "distance_limit_min"),
      distance_limit_mid: value("piracy", "distance_limit_mid"),
      distance_limit_max: value("piracy", "distance_limit_max"),
    },
  };
}

const serialize = value => {
  if (value == null) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Builds the `SettingsService.updateSettings` payload from the settings that
 * differ from the loaded values; unchanged settings are left out.
 *
 * Args:
 *   values: The submitted form values, with any pending voicemail file
 *     already replaced by its uploaded attachment record.
 *   initialValues: The values the form was loaded with.
 *
 * Returns:
 *   A list of `{ category, name, value }` entries with string values.
 */
export function toSettingsPayload(values, initialValues) {
  return Object.entries(values).flatMap(([category, fields]) =>
    Object.entries(fields)
      .filter(
        ([name, value]) => !isEqual(value, initialValues[category]?.[name])
      )
      .map(([name, value]) => ({ category, name, value: serialize(value) }))
  );
}
